'use client';

import type { NavListProps, NavSubListProps, NavItemDataProps } from '../types';

import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useEffect, useCallback } from 'react';
import { isActiveLink, isExternalLink } from 'minimal-shared/utils';

import { usePathname } from 'src/routes/hooks';

import { NavItem } from './nav-item';
import { navSectionClasses } from '../styles';
import { NavUl, NavLi, NavCollapse } from '../components';

// ----------------------------------------------------------------------

function ärAktiv(item: NavItemDataProps, pathname: string): boolean {
  return typeof item.path === 'string'
    ? isActiveLink(pathname, item.path, item.deepMatch ?? !!item.children)
    : false;
}

/**
 * Om den aktuella sidan ligger någonstans i den här grenen.
 *
 * Öppet-läget får inte hänga på förälderns egen `path`. En förälder pekar ofta
 * på en av sina undersidor snarare än på ett gemensamt prefix — "Boka & låna"
 * går till tvättstugan, medan Utlåningspool ligger på en helt annan gren. Mäter
 * man bara förälderns path fälls hela menyn ihop så fort man klickar sig in i
 * ett syskon, vilket ser ut som att sidebaren laddas om.
 */
function innehållerAktivSida(item: NavItemDataProps, pathname: string): boolean {
  return (
    ärAktiv(item, pathname) ||
    !!item.children?.some((barn) => innehållerAktivSida(barn, pathname))
  );
}

export function NavList({
  data,
  depth,
  render,
  slotProps,
  checkPermissions,
  enabledRootRedirect,
}: NavListProps) {
  const pathname = usePathname();
  const navItemRef = useRef<HTMLButtonElement>(null);

  // Markeringen följer postens egen path; utfällningen följer hela grenen.
  const isActive = typeof pathname === 'string' ? ärAktiv(data, pathname) : false;
  const harAktivSida = typeof pathname === 'string' && innehållerAktivSida(data, pathname);

  const { value: open, onFalse: onClose, onTrue: onOpen, onToggle } = useBoolean(harAktivSida);

  useEffect(() => {
    if (harAktivSida) {
      onOpen();
    } else {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      if (!enabledRootRedirect || isActive) {
        onToggle();
      }
    }
  }, [data.children, enabledRootRedirect, isActive, onToggle]);

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      open={open}
      active={isActive}
      disabled={data.disabled}
      // options
      depth={depth}
      render={render}
      hasChild={!!data.children}
      externalLink={typeof data.path === 'string' ? isExternalLink(data.path) : false}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onClick={handleToggleMenu}
    />
  );

  const renderCollapse = () =>
    !!data.children && (
      <NavCollapse mountOnEnter unmountOnExit depth={depth} in={open} data-group={data.title}>
        <NavSubList
          data={data.children}
          render={render}
          depth={depth}
          slotProps={slotProps}
          checkPermissions={checkPermissions}
          enabledRootRedirect={enabledRootRedirect}
        />
      </NavCollapse>
    );

  // Hidden item by role
  if (data.allowedRoles && checkPermissions && !checkPermissions(data.allowedRoles)) {
    return null;
  }

  return (
    <NavLi
      disabled={data.disabled}
      sx={{
        ...(!!data.children && {
          [`& .${navSectionClasses.li}`]: {
            '&:first-of-type': { mt: 'var(--nav-item-gap)' },
          },
        }),
      }}
    >
      {renderNavItem()}
      {renderCollapse()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  render,
  depth = 0,
  slotProps,
  checkPermissions,
  enabledRootRedirect,
}: NavSubListProps) {
  return (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          checkPermissions={checkPermissions}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  );
}
